import { type JSX, type SVGProps } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import { AssetValueLabel } from '@/shared/components/dropdown/filter/ActiveFilters';
import { QuickAction } from '@/shared/components/dropdown/filter/FilterDropdown';
import { type SwapStatus } from '@/shared/graphql/generated/reporting/graphql';
import useSupportedAssets from '@/shared/hooks/useSupportedAssets';
import ArrowIcon from '@/shared/icons/flip-ui-kit/large/Arrow';
import BrokerIcon from '@/shared/icons/flip-ui-kit/large/Broker';
import DottedIcon from '@/shared/icons/flip-ui-kit/large/Dotted';
import FlagIcon from '@/shared/icons/flip-ui-kit/large/Flag';
import SendIcon from '@/shared/icons/flip-ui-kit/large/Send';
import SettingsSlidersIcon from '@/shared/icons/flip-ui-kit/large/SettingsSliders';
import ShieldTick from '@/shared/icons/flip-ui-kit/large/ShieldTick';
import { formatWithNumeral } from '@/shared/utils/numbers';
import {
  AddressSelection,
  AssetSelection,
  BrokerSelection,
  SwapSizeSelection,
  swapStateOptions,
  SwapStateSelection,
  swapTypeOptions,
  SwapTypeSelection,
} from 'packages/block-explorer/components/SwapsPage/AllSwapsFilter/FilterSelections';
import {
  useAllSwapsFilterStore,
  type AllSwapsStoreData as StoreData,
} from 'packages/block-explorer/hooks/useFilterStores';

export type FilterOptionStore = Omit<StoreData, 'startDate' | 'endDate'>;

const isMultipleArray = (value: unknown): value is unknown[] =>
  Array.isArray(value) && value.length >= 2;

export const allSwapsFilters = [
  {
    type: 'swapStates',
    label: 'Status',
    icon: DottedIcon,
  },
  {
    type: 'swapSizeRange',
    label: 'Swap size',
    icon: SettingsSlidersIcon,
  },
  {
    type: 'assetsFrom',
    label: 'Asset from',
    icon: ArrowIcon as (props: SVGProps<SVGSVGElement>) => JSX.Element,
  },
  {
    type: 'assetsTo',
    label: 'Asset to',
    icon: ArrowIcon as (props: SVGProps<SVGSVGElement>) => JSX.Element,
  },
  {
    type: 'swapTypes',
    label: 'Type',
    icon: FlagIcon,
  },
  {
    type: 'brokers',
    label: 'Broker',
    icon: BrokerIcon,
  },
  {
    type: 'refundAddress',
    label: 'Refund address',
    icon: ShieldTick,
  },
  {
    type: 'destinationAddress',
    label: 'Destination address',
    icon: SendIcon,
  },
] as const;

export const selectedFilter = (activeFilter: (typeof allSwapsFilters)[number]['type']) => {
  switch (activeFilter) {
    case 'swapStates':
      return <SwapStateSelection />;
    case 'swapSizeRange':
      return <SwapSizeSelection />;
    case 'assetsFrom':
      return <AssetSelection direction="from" />;
    case 'assetsTo':
      return <AssetSelection direction="to" />;
    case 'destinationAddress':
      return <AddressSelection type="destination" />;
    case 'refundAddress':
      return <AddressSelection type="refund" />;
    case 'brokers':
      return <BrokerSelection />;
    case 'swapTypes':
      return <SwapTypeSelection />;
    default:
      return null;
  }
};

export const isLabel = <T extends keyof FilterOptionStore>(
  type: T,
  value: NonNullable<FilterOptionStore[T]>,
) => {
  switch (type) {
    case 'swapStates':
    case 'brokers':
      return value.length >= 2 ? 'is any of' : 'is';
    case 'swapTypes':
      return value.length >= 2 ? 'is of' : 'is';
    case 'assetsFrom':
    case 'assetsTo':
      return value.length >= 2 ? 'includes any of' : 'is';
    case 'swapSizeRange':
      return 'in between';
    default:
      return 'is';
  }
};

const SwapStatesValueLabel = ({ swapStates }: { swapStates: NonNullable<SwapStatus>[] }) => {
  if (swapStates.length === 1) {
    const swapState = swapStateOptions.find((option) => option.value === swapStates[0]);
    return (
      <div className="flex items-center gap-x-1">
        {swapState?.icon({ className: 'h-[16px] w-[16px]' })}
        <div>{swapState?.label}</div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-x-1">
      <div className="flex items-center -space-x-1">
        {swapStates.slice(0, 3).map((swapState) => (
          <div key={swapState}>
            {swapStateOptions
              .find((option) => option.value === swapState)
              ?.icon({ className: 'h-[16px] w-[16px]' })}
          </div>
        ))}
      </div>
      <div>{swapStates.length} statuses</div>
    </div>
  );
};

export const valueLabel = <T extends keyof FilterOptionStore>(
  type: T,
  value: NonNullable<FilterOptionStore[T]>,
) => {
  switch (type) {
    case 'swapStates':
      return <SwapStatesValueLabel swapStates={value as FilterOptionStore['swapStates']} />;
    case 'swapSizeRange':
      return `$${formatWithNumeral(value?.[0])} - $${formatWithNumeral(value[1])}`;
    case 'brokers':
      return isMultipleArray(value) ? `${value.length} brokers` : abbreviate(value[0]);
    case 'assetsFrom':
    case 'assetsTo':
      return <AssetValueLabel assets={value as FilterOptionStore['assetsFrom']} />;
    case 'refundAddress':
    case 'destinationAddress':
      return abbreviate(value as string);
    case 'swapTypes':
      return isMultipleArray(value)
        ? `${value.length} types`
        : swapTypeOptions.find((option) => option.value === value[0])?.label;

    default:
      return value;
  }
};

export const quickAction = (activeFilter: keyof FilterOptionStore) => {
  const { resetFilter, assetsFrom, assetsTo, setFilter, swapSizeRange } = useAllSwapsFilterStore();
  const chainflipAssets = useSupportedAssets();

  switch (activeFilter) {
    case 'swapSizeRange':
      return (
        <QuickAction
          text="Reset"
          onClick={() => resetFilter('swapSizeRange')}
          className={swapSizeRange[0] === 0 && swapSizeRange[1] === 0 ? 'hidden' : ''}
        />
      );
    case 'assetsFrom':
      return (
        <QuickAction
          text={assetsFrom.length === chainflipAssets.length ? 'Unselect all' : 'Select all'}
          onClick={() =>
            setFilter(
              'assetsFrom',
              assetsFrom.length === chainflipAssets.length ? [] : [...chainflipAssets],
            )
          }
        />
      );
    case 'assetsTo':
      return (
        <QuickAction
          text={assetsTo.length === chainflipAssets.length ? 'Unselect all' : 'Select all'}
          onClick={() =>
            setFilter(
              'assetsTo',
              assetsTo.length === chainflipAssets.length ? [] : [...chainflipAssets],
            )
          }
        />
      );
    default:
      return null;
  }
};
